import Conf from "conf"
import type { Logger } from "./logger"

export interface TokenConfig {
  bearerToken?: string
  authToken?: string
  email?: string
  loginDate?: string
  plans?: string[]
  licenseAccepted?: boolean
  licenseAcceptedDate?: string
}

export class TokenStorage {
  private config: Conf<TokenConfig>
  private logger: Logger

  constructor(logger: Logger) {
    this.logger = logger
    this.config = new Conf<TokenConfig>({
      projectName: "tiptap-cli",
      clearInvalidConfig: true, // auto-reset if file is corrupted
      schema: {
        bearerToken: { type: "string" },
        authToken: { type: "string" },
        email: { type: "string" },
        loginDate: { type: "string" },
        plans: { type: "array", items: { type: "string" }, default: [] },
        licenseAccepted: { type: "boolean" },
        licenseAcceptedDate: { type: "string" },
      },
    })
  }

  private safeError(prefix: string, error: unknown) {
    const msg =
      error instanceof Error
        ? `${error.name}: ${error.message}`
        : String(error ?? "Unknown error")
    try {
      this.logger.error(`${prefix}: ${msg}`)
    } catch {
      // never throw from logging
    }
  }

  setBearerToken(
    token: string,
    userInfo?: { email?: string; plans?: string[] }
  ): void {
    try {
      this.config.set("bearerToken", token)
      this.config.set("loginDate", new Date().toISOString())
      if (userInfo?.email) this.config.set("email", userInfo.email)
      if (userInfo?.plans) this.config.set("plans", userInfo.plans)
    } catch (e) {
      this.safeError("Failed to store bearer token", e)
    }
  }

  getBearerToken(): string | null {
    try {
      return this.config.get("bearerToken") ?? null
    } catch (e) {
      this.safeError("Failed to retrieve bearer token", e)
      return null
    }
  }

  setAuthToken(
    token: string,
    userInfo?: { email?: string; plans?: string[] }
  ): void {
    try {
      this.config.set("authToken", token)
      this.config.set("loginDate", new Date().toISOString())
      if (userInfo?.email) this.config.set("email", userInfo.email)
      if (userInfo?.plans) this.config.set("plans", userInfo.plans)
    } catch (e) {
      this.safeError("Failed to store auth token", e)
    }
  }

  getAuthToken(): string | null {
    try {
      return this.config.get("authToken") ?? null
    } catch (e) {
      this.safeError("Failed to retrieve auth token", e)
      return null
    }
  }

  getUserInfo(): { email?: string; plans: string[]; loginDate?: string } {
    return {
      email: this.config.get("email"),
      plans: this.config.get("plans") ?? [],
      loginDate: this.config.get("loginDate"),
    }
  }

  getTokenExpiration(): string | null {
    const loginDate = this.config.get("loginDate")
    if (!loginDate) return null
    const expiration = new Date(loginDate)
    expiration.setMonth(expiration.getMonth() + 11)

    return expiration.toISOString()
  }

  isValidToken(): boolean {
    const bearerToken = this.getBearerToken()
    const authToken = this.getAuthToken()

    if (!bearerToken && !authToken) return false

    const expirationISO = this.getTokenExpiration()
    if (expirationISO) {
      const expiryDate = new Date(expirationISO)
      if (expiryDate <= new Date()) {
        this.clear()
        return false
      }
    }

    return true
  }

  clear(): void {
    try {
      this.config.clear()
    } catch (e) {
      this.safeError("Failed to clear token storage", e)
    }
  }

  hasPlan(planName: string): boolean {
    const plans = this.config.get("plans") ?? []
    return Array.isArray(plans) && plans.includes(planName)
  }

  setLicenseAccepted(): void {
    try {
      this.config.set("licenseAccepted", true)
      this.config.set("licenseAcceptedDate", new Date().toISOString())
    } catch (e) {
      this.safeError("Failed to store license acceptance", e)
    }
  }

  hasAcceptedLicense(): boolean {
    try {
      return this.config.get("licenseAccepted") ?? false
    } catch (e) {
      this.safeError("Failed to retrieve license acceptance", e)
      return false
    }
  }

  clearLicenseAcceptance(): void {
    try {
      this.config.delete("licenseAccepted")
      this.config.delete("licenseAcceptedDate")
    } catch (e) {
      this.safeError("Failed to clear license acceptance", e)
    }
  }
}
